import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  MediaLibraryCategoryEnum,
  MediaLibraryFileModel,
  MediaLibraryUploadType,
} from 'src/app/models/sales-funnel';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { InOutAnimation } from 'src/app/shared/animations/animations';

interface VideoParseResult {
  provider: 'youtube' | 'vimeo' | 'direct' | 'custom' | 'unknown';
  embedUrl: string | null;
  id?: string;
}

@Component({
  selector: 'app-videourl-or-media',
  templateUrl: './videourl-or-media.component.html',
  styleUrls: ['./videourl-or-media.component.css'],
  animations: [InOutAnimation]
})
export class VideourlOrMediaComponent implements OnInit, OnDestroy {
  @Input() label: { text: string; required: boolean } = { text: 'Video', required: false };
  @Input() videoSource: 'url' | 'file' = 'url';
  @Output() videoSourceChange = new EventEmitter<'url' | 'file'>();

  @Input() videoUrl?: string;
  @Output() videoUrlChange = new EventEmitter<string>();

  @Input() video?: Partial<MediaLibraryFileModel>[];
  @Output() videoChange = new EventEmitter<Partial<MediaLibraryFileModel>[]>();

  @Input() videoId?: number[];
  @Output() videoIdChange = new EventEmitter<number[]>();

  // Preview state
  videoPreviewUrl?: SafeResourceUrl;
  videoURLProcessResult: VideoParseResult | null = null;
  isValidUrl = false;
  isProcessing = false;

  // Enums for template use
  mediaLibraryUploadType = MediaLibraryUploadType;
  mediaLibraryCategoryEnum = MediaLibraryCategoryEnum;

  // Private helpers
  private destroy$ = new Subject<void>();
  private urlInputSubject = new Subject<string>();

  private readonly urlPatterns = {
    youtube: /(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([^"&?\/\s]{11})/i,
    vimeo: /vimeo\.com.*(?:videos|video|channels|)\/([\d]+)/i,
    direct: /^https?:\/\/.*\.(mp4|webm|ogg|mov|avi|wmv|flv|m4v)(\?.*)?$/i,
  };

  constructor(private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    // Debounced URL input
    this.urlInputSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((url) => this.processVideoUrl(url));

    // Initialize preview
    if (this.videoSource === 'url' && this.videoUrl) {
      this.processVideoUrl(this.videoUrl);
    } else if (this.videoSource === 'file' && this.video?.length) {
      this.updateFilePreview();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** ========== Source Selection ========== */
  selectVideoType(type: 'url' | 'file') {
    if (this.videoSource !== type) {
      this.videoSource = type;
      this.videoSourceChange.emit(this.videoSource);
      this.updatePreview(type, this.videoUrl, this.video);
    }
  }

  /** ========== URL Handling ========== */
  onVideoUrlInput(url: string) {
    this.videoUrl = url.trim();
    this.videoUrlChange.emit(this.videoUrl);
    if (this.videoUrl) {
      this.isProcessing = true;
      this.urlInputSubject.next(this.videoUrl);
    } else {
      this.clearPreview();
    }
  }

  private parseVideoUrl(url: string): VideoParseResult {
    if (!url) return { provider: 'unknown', embedUrl: null };

    const ytMatch = url.match(this.urlPatterns.youtube);
    if (ytMatch) {
      return {
        provider: 'youtube',
        id: ytMatch[1],
        embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`,
      };
    }

    const vimeoMatch = url.match(this.urlPatterns.vimeo);
    if (vimeoMatch) {
      return {
        provider: 'vimeo',
        id: vimeoMatch[1],
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?title=0&byline=0&portrait=0`,
      };
    }

    if (this.urlPatterns.direct.test(url)) {
      return { provider: 'direct', embedUrl: url };
    }

    if (/^https?:\/\/.+/.test(url)) {
      return { provider: 'custom', embedUrl: url };
    }

    return { provider: 'unknown', embedUrl: null };
  }

  private processVideoUrl(url: string) {
    if (url) {
      this.videoURLProcessResult = this.parseVideoUrl(url);
      this.videoPreviewUrl = this.videoURLProcessResult?.embedUrl || undefined;
      this.isValidUrl = !!this.videoPreviewUrl;
      this.isProcessing = false;
    } else {
      this.clearPreview();
    }
  }

  clearVideoUrl() {
    this.videoUrl = '';
    this.videoUrlChange.emit('');
    this.clearPreview();
  }

  /** ========== File Handling ========== */
  onVideoFileChange(files: Partial<MediaLibraryFileModel>[]) {
    this.video = files;
    this.videoChange.emit(files);
    this.updateFilePreview();
  }

  private updateFilePreview() {
    if (this.video?.length && (this.video[0] as any)?.url) {
      this.videoPreviewUrl = (this.video[0] as any).url;
      this.isValidUrl = true;
    } else {
      this.clearPreview();
    }
  }


  /** ========== Shared Helpers ========== */
  private updatePreview(videoSource: 'url' | 'file', videoUrl: string, video: Partial<MediaLibraryFileModel>[]) {
    console.log(videoSource, videoUrl, video);
    if (videoSource === 'url' && videoUrl) {
      this.processVideoUrl(videoUrl);
    } else if (videoSource === 'file' && video?.length) {
      this.updateFilePreview();
    } else {
      this.clearPreview();
    }
  }

  removeVideo() {
    if (this.videoSource === 'url') {
      this.clearVideoUrl();
    } else {
      this.video = [];
      this.videoChange.emit([]);
      this.clearPreview();
    }
  }

  private clearPreview() {
    this.videoPreviewUrl = undefined;
    this.isValidUrl = false;
    this.isProcessing = false;
  }

  /** ========== Getters for Template ========== */
  get hasVideoContent(): boolean {
    return !!this.videoPreviewUrl;
  }

  get videoSourceValue(): string {
    if (this.videoSource === 'url') return this.videoUrl || '';
    if (this.video?.length) return (this.video[0] as any)?.name || 'Uploaded video file';
    return '';
  }

  get videoTypeLabel(): string {
    return this.videoSource === 'url' ? 'Embedded video' : 'Uploaded video';
  }
}
